<template>
  <div
    class="survey-slider"
    :class="{
      'survey-slider--untouched': !isAnswered,
      'survey-slider--has-ticks': showTicks,
      'survey-slider--invalid': state === false,
      'survey-slider--attention': attentionWhenEmpty,
    }"
  >
    <!-- Read-out compatto del valore: feedback live che si aggiorna durante il trascinamento -->
    <div class="survey-slider__readout" aria-hidden="true">
      <span class="survey-slider__readout-value">{{ valueLabel }}</span>
    </div>

    <!-- Contenitore traccia: ospita il range nativo e, sovrapposte, le tacche -->
    <div class="survey-slider__track-wrap">
      <!-- La custom property --range-percent pilota il riempimento del brand su webkit,
           privo di pseudo-elemento di progress nativo (firefox usa ::-moz-range-progress) -->
      <b-form-input
        type="range"
        class="survey-slider__range"
        :style="trackStyle"
        :value="currentValue"
        :min="min"
        :max="max"
        :step="step"
        :name="name"
        :state="state"
        :disabled="disabled"
        :aria-label="ariaLabel || $t('SurveySliderInput.selettore_valore')"
        :aria-valuetext="ariaValueText"
        @input="onInput"
      ></b-form-input>

      <!-- Tacche numerate per le scale discrete (tipico 0-10 dei sintomi):
           rendono la traccia una scala percepibile a colpo d'occhio -->
      <div v-if="showTicks" class="survey-slider__ticks" aria-hidden="true">
        <span
          v-for="tick in ticks"
          :key="tick.value"
          class="survey-slider__tick"
          :class="{ 'survey-slider__tick--active': isTickActive(tick.value) }"
          :style="tickStyle(tick.frac)"
        >
          <span class="survey-slider__tick-mark"></span>
          <small v-if="tick.showLabel" class="survey-slider__tick-label">{{
            tick.value
          }}</small>
        </span>
      </div>
    </div>

    <!-- Scala min/max di riferimento per le scale continue prive di tacche -->
    <div v-if="!showTicks" class="survey-slider__scale" aria-hidden="true">
      <small class="text-muted">{{ min }}</small>
      <small class="text-muted">{{ max }}</small>
    </div>
  </div>
</template>

<script>
// Slider generico riusabile dai compilatori questionario. Il range nativo emette
// sempre stringhe: l'output viene castato a numero (intero o decimale secondo rc.isDecimal)
// prima di propagarlo come v-model. Bounds e passo arrivano da rc {min,max,step,isDecimal}.
//
// Affordance e feedback: il thumb e' grande e ad alto contrasto (manopola da afferrare,
// cursore grab/grabbing), la traccia mostra il riempimento del brand fin dove e' arrivato
// il cursore e, per le scale discrete, compaiono tacche numerate. Il read-out e l'eventuale
// tacca attiva danno il feedback del valore mentre si sposta il cursore.

// Oltre questo numero di punti non disegniamo le tacche: scale troppo fitte risulterebbero
// illeggibili e affollate (es. 0-100 step 1).
const MAX_TICKS = 11;

export default {
  name: "SurveySliderInput",
  props: {
    value: { type: [Number, String], default: null },
    rc: { type: Object, default: null },
    name: { type: String, default: null },
    ariaLabel: { type: String, default: null },
    state: { type: Boolean, default: null },
    disabled: { type: Boolean, default: false },
    // Animazione di richiamo (pulsazione del thumb) quando il valore e' ancora vuoto.
    // Disattivata di default: va abilitata solo dove ha senso invitare il cittadino a
    // rispondere (es. questionario sintomi), mai nelle viste di edit dell'operatore.
    attentionWhenEmpty: { type: Boolean, default: false },
  },
  computed: {
    min() {
      return this.rc && this.rc.min != null ? Number(this.rc.min) : 0;
    },
    max() {
      return this.rc && this.rc.max != null ? Number(this.rc.max) : 100;
    },
    step() {
      return this.rc && this.rc.step != null ? Number(this.rc.step) : 1;
    },
    isDecimal() {
      return !!(this.rc && this.rc.isDecimal);
    },
    // Valore mostrato allo slider: se non ancora risposto si posiziona sul minimo
    currentValue() {
      return this.value != null && this.value !== "" ? this.value : this.min;
    },
    // True solo quando il cittadino ha effettivamente interagito col cursore
    isAnswered() {
      return this.value != null && this.value !== "";
    },
    // Etichetta del valore corrente: trattino finche' il cittadino non interagisce
    valueLabel() {
      return this.isAnswered ? this.value : "—";
    },
    // aria-valuetext: il range nativo annuncia gia' il valore numerico al cambio.
    // Qui esponiamo lo stato "non risposto" ai lettori di schermo per mantenere la
    // parita' informativa con il trattino visibile (altrimenti annuncerebbe "0").
    ariaValueText() {
      return this.isAnswered
        ? String(this.value)
        : this.$t("SurveySliderInput.non_risposto");
    },
    // Percentuale di avanzamento del cursore, usata dal riempimento della traccia.
    // Protetta contro range degeneri e clampata tra 0 e 100.
    percent() {
      const span = this.max - this.min;
      if (!span || Number.isNaN(span)) {
        return 0;
      }
      const pct = ((Number(this.currentValue) - this.min) / span) * 100;
      return Math.min(100, Math.max(0, pct));
    },
    // Inline style che espone la percentuale al CSS (necessaria su webkit per il gradiente)
    trackStyle() {
      return { "--range-percent": this.percent + "%" };
    },
    // Numero di scalini discreti della scala (es. 0-10 step 1 => 10 scalini, 11 punti)
    stepsCount() {
      const span = this.max - this.min;
      if (!span || this.step <= 0) {
        return 0;
      }
      return span / this.step;
    },
    // Tacche visibili solo per scale intere e non troppo fitte: tipicamente i sintomi 0-10.
    // Per scale continue/decimali o molto ampie restano nascoste.
    showTicks() {
      if (this.isDecimal) {
        return false;
      }
      const count = this.stepsCount;
      return Number.isInteger(count) && count >= 1 && count + 1 <= MAX_TICKS;
    },
    // Elenco delle tacche con frazione 0-1 lungo la corsa utile del cursore
    ticks() {
      const out = [];
      const count = this.stepsCount;
      if (!count) {
        return out;
      }
      for (let i = 0; i <= count; i++) {
        const v = this.min + i * this.step;
        out.push({
          value: v,
          // Frazione della corsa: usata dal calc che allinea la tacca al centro del thumb
          frac: count ? i / count : 0,
          // Etichetta numerica: su scale piccole tutte, su scale piu ampie solo estremi e pari
          showLabel:
            count <= 6 || v === this.min || v === this.max || v % 2 === 0,
        });
      }
      return out;
    },
  },
  methods: {
    onInput(val) {
      if (val === null || val === "") {
        this.$emit("input", null);
        return;
      }
      const num = this.isDecimal ? parseFloat(val) : parseInt(val, 10);
      this.$emit("input", Number.isNaN(num) ? null : num);
    },
    // Posizione della tacca allineata alla corsa reale del thumb nativo, che si muove
    // tra meta' thumb e (larghezza - meta' thumb). Vale identica su webkit e firefox.
    tickStyle(frac) {
      return {
        left: `calc(var(--thumb-size) / 2 + ${frac} * (100% - var(--thumb-size)))`,
      };
    },
    // Una tacca e' "attiva" quando coincide con il valore scelto: evidenziata nel brand
    isTickActive(tickValue) {
      return this.isAnswered && Number(this.value) === Number(tickValue);
    },
  },
};
</script>

<style lang="scss" scoped>
// Le variabili di tema ($primary, $secondary, $sec-4, ...) sono iniettate globalmente da
// vue.config.js (prependData del file _variables dello scope attivo), quindi disponibili
// senza @import. NB: $primary-darker NON esiste in tutti gli scope: per le tonalita' piu
// scure si usa darken($primary, ...), valido su qualsiasi tema.

// Dimensioni condivise tra webkit e firefox per un aspetto identico cross-browser
$slider-thumb-size: 30px; // ampio per il touch dei pazienti anziani (oltre i 24px richiesti)
$slider-track-height: 12px; // traccia spessa e ben visibile
$slider-track-radius: 8px;
// Grigi neutri (non brand) della traccia: il bordo delinea il controllo con contrasto
// adeguato sullo sfondo chiaro, il riempimento del brand resta il riferimento di posizione
$slider-track-unfilled: #d3d8d6;
$slider-track-border: #8a938f;

.survey-slider {
  width: 100%;
  // Espone la misura del thumb al calc che posiziona le tacche (unica fonte: la var SCSS)
  --thumb-size: #{$slider-thumb-size};
}

// Read-out del valore corrente: principale feedback visivo durante il trascinamento
.survey-slider__readout {
  display: flex;
  justify-content: center;
  align-items: baseline;
  min-height: 1.75rem;
  margin-bottom: 0.35rem;
  line-height: 1;
}

.survey-slider__readout-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: $secondary;
  transition: color 0.15s ease;
}

// Finche' la domanda non e' risposta il valore resta neutro (trattino in grigio)
.survey-slider--untouched .survey-slider__readout-value {
  color: $font-light-grey;
}

// Contenitore della traccia: range nativo e tacche sovrapposte condividono la stessa larghezza
.survey-slider__track-wrap {
  position: relative;
  width: 100%;
}

// =====================================================================
// RANGE NATIVO - stile cross-browser (webkit + firefox)
// Bootstrap applica la classe .custom-range al range e ne stila gli pseudo-elementi:
// le regole sotto vincono grazie alla maggiore specificita' dell'attributo scoped [data-v].
// =====================================================================
.survey-slider__range {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  height: $slider-thumb-size; // area cliccabile alta quanto il thumb
  margin: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  // ---- Traccia: WEBKIT ----
  // Riempimento del brand simulato con un gradiente pilotato da --range-percent,
  // perche' webkit non espone uno pseudo-elemento di progress.
  &::-webkit-slider-runnable-track {
    height: $slider-track-height;
    border-radius: $slider-track-radius;
    border: 1px solid $slider-track-border;
    background: linear-gradient(
      to right,
      $primary 0%,
      $primary var(--range-percent, 0%),
      $slider-track-unfilled var(--range-percent, 0%),
      $slider-track-unfilled 100%
    );
  }

  // ---- Traccia: FIREFOX ----
  &::-moz-range-track {
    height: $slider-track-height;
    border-radius: $slider-track-radius;
    border: 1px solid $slider-track-border;
    background: $slider-track-unfilled;
  }

  // Firefox dispone dello pseudo-elemento di progress nativo: lo coloriamo col brand
  &::-moz-range-progress {
    height: $slider-track-height;
    border-radius: $slider-track-radius 0 0 $slider-track-radius;
    background: $primary;
  }

  // ---- Thumb: WEBKIT ----
  // Grande, ad alto contrasto, anello bianco + alone: comunica "afferrami e spostami"
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: $slider-thumb-size;
    height: $slider-thumb-size;
    // Centra il thumb sulla traccia piu sottile
    margin-top: calc((#{$slider-track-height} - #{$slider-thumb-size}) / 2);
    border-radius: 50%;
    background: $primary;
    border: 4px solid #fff;
    box-shadow: 0 0 0 2px darken($primary, 12%), 0 2px 6px rgba(0, 0, 0, 0.35);
    cursor: grab;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }

  // ---- Thumb: FIREFOX ----
  &::-moz-range-thumb {
    width: $slider-thumb-size;
    height: $slider-thumb-size;
    border-radius: 50%;
    background: $primary;
    border: 4px solid #fff;
    box-shadow: 0 0 0 2px darken($primary, 12%), 0 2px 6px rgba(0, 0, 0, 0.35);
    cursor: grab;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
  }

  // ---- Feedback da tastiera: il thumb cresce e mostra l'anello di focus ad alto contrasto ----
  &:focus {
    outline: none; // sostituito dall'anello su box-shadow; la modalita' a11y globale (outline !important) resta prioritaria
  }
  &:focus-visible::-webkit-slider-thumb {
    transform: scale(1.08);
    box-shadow: 0 0 0 4px $secondary, 0 2px 6px rgba(0, 0, 0, 0.35);
  }
  &:focus-visible::-moz-range-thumb {
    transform: scale(1.08);
    box-shadow: 0 0 0 4px $secondary, 0 2px 6px rgba(0, 0, 0, 0.35);
  }

  // ---- Feedback in trascinamento (mouse/touch): cursore grabbing e thumb ingrandito ----
  &:active::-webkit-slider-thumb {
    cursor: grabbing;
    transform: scale(1.18);
    box-shadow: 0 0 0 3px darken($primary, 12%), 0 4px 10px rgba(0, 0, 0, 0.4);
  }
  &:active::-moz-range-thumb {
    cursor: grabbing;
    transform: scale(1.18);
    box-shadow: 0 0 0 3px darken($primary, 12%), 0 4px 10px rgba(0, 0, 0, 0.4);
  }

  // ---- Stato di errore (state === false): bordo della traccia in danger ----
  .survey-slider--invalid &::-webkit-slider-runnable-track {
    border-color: $danger;
  }
  .survey-slider--invalid &::-moz-range-track {
    border-color: $danger;
  }
}

// =====================================================================
// RICHIAMO PER STATO NON RISPOSTO (opt-in via prop attentionWhenEmpty)
// Lieve pulsazione dell'alone del thumb, solo finche' il valore e' vuoto e mai su
// slider disabilitati. Disattivata sotto prefers-reduced-motion.
// =====================================================================
.survey-slider--attention.survey-slider--untouched
  .survey-slider__range:not(:disabled) {
  &::-webkit-slider-thumb {
    animation: survey-slider-nudge 2.4s ease-in-out infinite;
  }
  &::-moz-range-thumb {
    animation: survey-slider-nudge 2.4s ease-in-out infinite;
  }
}

@keyframes survey-slider-nudge {
  0%,
  100% {
    box-shadow: 0 0 0 2px darken($primary, 12%), 0 2px 6px rgba(0, 0, 0, 0.35);
  }
  50% {
    box-shadow: 0 0 0 7px rgba($primary, 0.3), 0 2px 8px rgba(0, 0, 0, 0.35);
  }
}

// =====================================================================
// TACCHE NUMERATE (scala a N punti, tipico 0-10 dei sintomi)
// =====================================================================
.survey-slider__ticks {
  position: relative;
  width: 100%;
  height: 1.6rem;
  margin-top: 0.35rem;
}

.survey-slider__tick {
  position: absolute;
  top: 0;
  // left e' calcolato inline sulla corsa utile del thumb; qui centriamo la colonna
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.survey-slider__tick-mark {
  width: 2px;
  height: 8px;
  border-radius: 2px;
  background: $slider-track-border;
}

.survey-slider__tick-label {
  margin-top: 0.15rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: $font-grey;
  white-space: nowrap;
}

// Tacca corrispondente al valore scelto: evidenziata nel colore secondario del brand
.survey-slider__tick--active {
  .survey-slider__tick-mark {
    background: $secondary;
    height: 11px;
    width: 3px;
  }
  .survey-slider__tick-label {
    color: $secondary;
    font-weight: 700;
  }
}

// =====================================================================
// SCALA SEMPLICE min/max (solo per scale continue senza tacche)
// Il padding laterale pari a meta' thumb allinea gli estremi alla corsa del cursore.
// =====================================================================
.survey-slider__scale {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem;
  padding: 0 calc(#{$slider-thumb-size} / 2);
}

// =====================================================================
// Rispetto di prefers-reduced-motion: nessuna animazione o trasformazione
// =====================================================================
@media (prefers-reduced-motion: reduce) {
  .survey-slider__readout-value,
  .survey-slider__range::-webkit-slider-thumb,
  .survey-slider__range::-moz-range-thumb {
    transition: none !important;
  }
  .survey-slider--attention.survey-slider--untouched .survey-slider__range {
    &::-webkit-slider-thumb,
    &::-moz-range-thumb {
      animation: none !important;
    }
  }
  .survey-slider__range:focus-visible::-webkit-slider-thumb,
  .survey-slider__range:focus-visible::-moz-range-thumb,
  .survey-slider__range:active::-webkit-slider-thumb,
  .survey-slider__range:active::-moz-range-thumb {
    transform: none;
  }
}
</style>
